import { IAuthenticatedAccount, IRegion, IUser } from '@alberta/konexi-shared';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { OAuthService, TokenResponse } from 'angular-oauth2-oidc';
import { AuthActions, AuthService as MobileOAuthService } from 'ionic-appauth';
import { omit } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { MobileTokenPreservingAuthService } from 'src/app/auth/factories';
import { classifyRefreshError, RefreshErrorKind } from 'src/app/common/authentication/refresh-error';
import { IAuthEventData } from 'src/app/common/contracts/authentication/auth-event-data';
import {
  AuthOutcome,
  isAuthenticated as isAuthenticatedOutcome,
} from 'src/app/common/contracts/authentication/auth-outcome';
import { Deferred } from 'src/app/common/deferred/deferred';
import { isTokenAboutToExpire } from 'src/app/common/jwt/verifyJWT';

import hydratedOrganization from '@alberta/konexi-shared/dist/auth/hydrated-organization';
import { environment } from 'src/environments/environment.default';
import makeDebug from '../../../makeDebug';
import { readAccountId, validateAccount } from './account/account-validation';
import { ConnectionStateService } from './connection-state.service';
import { IConnectionStateService } from './contracts/sync/connection-state-service';
import { IFeathersAppProvider } from './contracts/sync/feathers-app-provider';
import { EnvironmentService } from './environment/environment.service';
import { FeathersService } from './feathers.service';
import { SessionVaultService } from './session-vault/session-vault.service';
import { storageDiagnostics, StorageDiagnosticsService } from './storage-diagnostics/storage-diagnostics.service';
import { TrackerService } from './tracker.service';

const debug = makeDebug('auth:auth');

/**
 * The token validity buffer is the time in seconds that is added to the token expiration time
 * to ensure that the token is still valid when it is used.
 * The formula is:  return now < this.issuedAt + this.expiresIn + buffer;
 * So if you enter -2*60 the token is valid if it has at least 2 minutes left.
 */
export const TOKEN_VALIDITY_BUFFER_IN_SECONDS = -2 * 60;

/**
 * Transient backend/network failures (timeout, bad gateway, unavailable, gateway timeout)
 * that justify retrying the Feathers authentication instead of failing the login.
 */
const TRANSIENT_AUTH_ERROR_CODES = new Set([408, 502, 503, 504]);
const MAX_TRANSIENT_AUTH_RETRIES = 2;
const AUTH_RETRY_BASE_DELAY_IN_MS = 1000;

function isTransientAuthError(error: unknown): boolean {
  const feathersError = error as { code?: number; className?: string };
  return (
    feathersError?.className === 'timeout' ||
    (feathersError?.code != null && TRANSIENT_AUTH_ERROR_CODES.has(feathersError.code))
  );
}

/**
 * Text of a failed OAuth refresh for classifyRefreshError() (#26615).
 *
 * angular-oauth2-oidc rejects with an HttpErrorResponse: the OAuth code sits in the body
 * (error.error.error), the message is generic — the classifier gets both, code first so its
 * anchored invalid_grant match still applies. Plain errors and strings pass through as their text.
 */
function describeRefreshError(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  const response = error as { error?: { error?: unknown; error_description?: unknown }; message?: unknown };
  return [response?.error?.error, response?.error?.error_description, response?.message]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ');
}

//#region INTERFACES

/**
 * Authentication result from Feathers backend after successful JWT authentication.
 * Contains the access token, JWT payload, and hydrated user account.
 */
export interface AlbertaAuthenticationResult {
  authentication?: {
    strategy?: string;
    accessToken?: string;
    payload?: {
      aud?: string;
      exp?: number;
      iat?: number;
      iss?: string;
      sub?: string;
      jti?: string;
      authenticationType?: string;
      tty?: string;
      email?: string;
      email_verified?: boolean;
      roles?: string[];
      auth_time?: number;
      scope?: string;
      applicationId?: string;
      tid?: string;
      sid?: string;
      gty?: string[];
    };
  };
  accessToken?: string;
  user?: IAuthenticatedAccount;
}

/** Whether any OAuth token material is present. `unknown` means the check could not complete. */
type TokenState = 'token-present' | 'no-token' | 'unknown';

export interface IAuthService {
  authenticate(isOnline: boolean): Promise<boolean>;
  authenticateWithOutcome(isOnline: boolean): Promise<AuthOutcome>;
  getAccessToken(): Promise<string | null>;
  refreshTokenMobile(): Promise<boolean>;
  tryRefresh(): Promise<boolean>;
  hasAnyToken(): Promise<boolean>;
  readonly hasSession: boolean;
  configure(): Promise<void>;
  login(email: string): Promise<void>;
  logout(skipWindowReload?: boolean): Promise<void>;
  getUserFullName(): Promise<string>;
  getCurrentUserICSToken(): Promise<string | null>;
  setAuthenticated(isAuthenticated: boolean, isOnline?: boolean): Promise<void>;
  getCurrentUserId(): Promise<string>;
  resetRights(rights: any): Promise<void>;
  resetCurrentUser(user: IUser, regions: IRegion[]): Promise<void>;
  getUserPermission(domain: string): Promise<number>;
  userHasPermission(domain: string, permission: number): Promise<boolean>;
  getCurrentUserGroups(): Promise<string[] | null>;
  getCurrentUserRegions(): Promise<IRegion[] | null>;
  getCurrentUserTenantName(): Promise<string | null>;
  getCurrentUserOrganization(): Promise<hydratedOrganization | null>;
  isTokenValid(): Promise<boolean>;
}

//#endregion

@Injectable({
  providedIn: 'root',
})
export class AuthService implements IAuthService {
  //#region PRIVATE PROPERTIES
  private _tokenExpiredSubscription: Subscription;
  // FUTURE TODO: We might delete _init and use accountReady instead.
  // They seem redudant but might be needed for sync integrity.
  // If you come here and feel brave. Do it and test it.
  private _init = new Deferred<void>();
  private _authenticationEventSubject = new BehaviorSubject<IAuthEventData>({
    isAuthenticated: false,
  });
  private _isAuthenticated$ = new BehaviorSubject<boolean>(false);
  private _mobileAccessToken: Deferred<string>;
  private _account$ = new BehaviorSubject<IAuthenticatedAccount | null>(null);
  private _accountReady = new Deferred<IAuthenticatedAccount>();

  private get isCordova(): boolean {
    return this._platform.is('hybrid');
  }
  private _refreshTokenPromise: Promise<boolean> | null = null;
  /** In-flight web refresh, see refreshWebTokenOnce() (#26615). */
  private _webRefreshPromise: Promise<TokenResponse> | null = null;
  private _configureMobilePromise: Promise<void> | null = null;
  //#endregion

  //#region PUBLIC PROPERTIES
  public init = this._init.promise;
  public redirectUrl: string;
  public authenticatedEventPublisher = this._authenticationEventSubject.asObservable();

  /**
   * Promise that resolves when the account is loaded.
   */
  public readonly accountReady = this._accountReady.promise;

  /**
   * Synchronous access to the current account. Returns null if not authenticated.
   */
  public async getAccount(): Promise<IAuthenticatedAccount | null> {
    await this.accountReady;
    if (!this._account$.value) {
      const errorMessage = 'Account was accesed. But was not defined in authService';
      window.logger.error(errorMessage, new Error(errorMessage));
    }
    return this._account$.value;
  }

  /**
   * Returns whether the user is currently authenticated.
   */
  public get isAuthenticated(): boolean {
    return this._isAuthenticated$.value;
  }

  /**
   * True when an account is available in memory, i.e. the running process has a usable session
   * regardless of what the backend or the storage currently say (#26615).
   */
  public get hasSession(): boolean {
    return this._account$.value != null;
  }

  /**
   * Current user's full name or null if not authenticated.
   */
  public async getUserFullName(): Promise<string> {
    const account = await this.getAccount();
    return `${account.firstName} ${account.lastName}`;
  }

  /**
   * Current user's email or null if not authenticated.
   */
  public async getUserEmail(): Promise<string> {
    const account = await this.getAccount();
    return account?.email;
  }

  /**
   * Checks the token validity without changing any state (#26615).
   *
   * `getValidToken()` triggers a refresh internally when the token is close to expiry, and a
   * failing refresh used to delete the token — a read-only question must not be able to
   * destroy the session it asks about. The session token is inspected directly instead; the
   * actual refresh stays where it belongs (refreshTokenMobile / observeTokenExpiration).
   */
  public async isTokenValid(): Promise<boolean> {
    if (!this.isCordova) {
      return this._WebOauthService.hasValidAccessToken();
    }
    try {
      const tokenResponse = this._mobileOAuthService.session?.token;
      return tokenResponse != null && tokenResponse.isValid(TOKEN_VALIDITY_BUFFER_IN_SECONDS);
    } catch {
      return false;
    }
  }

  /**
   * Current user's regions. Returns empty array if not authenticated.
   */
  public async getUserRegions(): Promise<IRegion[]> {
    const account = await this.getAccount();
    return account?.authorization?.regions;
  }

  public async getCalendarConnector(): Promise<string> {
    const account = await this.getAccount();
    return account?.authorization?.hydratedOrganization?.alberta?.calendarConnector;
  }

  /**
   * Current user's groups. Returns empty array if not authenticated.
   */
  public async getUserGroups(): Promise<string[]> {
    const account = await this.getAccount();
    return account?.groups;
  }

  /**
   * Users with the debug flag will send verbose debug information.
   * This flag should only be used temporarely and should be set to false asap.
   */
  public async isDebugUser(): Promise<boolean> {
    const account = await this.getAccount();
    return account.debug === true;
  }

  public async isAgent(): Promise<boolean> {
    const account = await this.getAccount();
    return account.isAgent === true;
  }

  public async setO365Id(o365Id: string): Promise<void> {
    const account = await this.getAccount();
    if (!account) {
      // getAccount() can legitimately resolve to null (after a logout the resolved deferred no
      // longer waits), and dereferencing it here threw an unhandled TypeError (#26615).
      window.logger.error('setO365Id without account', new Error('[26615] setO365Id: account is null'));
      return;
    }
    account.o365Id = o365Id;
    await this.persistAccount(account, 'setO365Id');
    this._account$.next(account);
  }

  public async getO365Id(): Promise<string> {
    const account = await this.getAccount();
    return account.o365Id;
  }

  /**
   * Current user's first name.
   */
  public async getUserFirstName(): Promise<string> {
    const account = await this.getAccount();
    return account?.firstName;
  }

  /**
   * Current user's last name.
   */
  public async getUserLastName(): Promise<string> {
    const account = await this.getAccount();
    return account?.lastName;
  }

  /**
   * Current user's mobile phone.
   */
  public async getUserMobilePhone(): Promise<string> {
    const account = await this.getAccount();
    return account?.mobilePhone;
  }

  public async userIsItLabs(): Promise<boolean> {
    const account = await this.getAccount();

    const userIsItLabs =
      environment.validITLabsUser.some(mailOrSuffixToCheck => account.email.includes(mailOrSuffixToCheck)) ||
      (account.canChangeChannel != null && Boolean(account.canChangeChannel));

    return userIsItLabs;
  }

  public async canCreateTenant(): Promise<boolean> {
    const account = await this.getAccount();

    const userIsItLabs = environment.validITLabsUser.some(mailOrSuffixToCheck =>
      account.email.includes(mailOrSuffixToCheck)
    );

    return userIsItLabs;
  }

  //#endregion

  constructor(
    @Inject(ConnectionStateService) private _connectionStateService: IConnectionStateService,
    private storage: Storage,
    @Inject(FeathersService) private _feathersAppProvider: IFeathersAppProvider,
    private _tracker: TrackerService,
    private _platform: Platform,
    private _WebOauthService: OAuthService,
    @Inject(MobileOAuthService) private _mobileOAuthService: MobileTokenPreservingAuthService,
    private _ngZone: NgZone,
    private _router: Router,
    private _sessionVault: SessionVaultService,
    private _environmentService: EnvironmentService
  ) {
    this._connectionStateService.connected.pipe(filter(isOnline => !isOnline)).subscribe(() => {
      this._authenticationEventSubject.next({ isAuthenticated: false });
    });
    if (this._platform.is('hybrid') === false) {
      this._WebOauthService.events
        .pipe(
          filter(event => event.type === 'token_received'),
          take(1),
          tap(event => {
            this.redirectUrl = decodeURIComponent(this._WebOauthService.state) || '/dashboard';
            if (this.redirectUrl !== '/dashboard' && this.redirectUrl !== '/login') {
              this._router.navigate([this.redirectUrl]);
            }
          })
        )
        .subscribe();
    }
  }

  //#region AUTHENTICATION METHODS

  /**
   * Authenticate the user.
   * @param isOnline - Whether the user is online or not.
   * @returns A promise that resolves to a boolean indicating whether the authentication was successful.
   * Call configure before authenticate or the token might be set wrong
   */
  public async authenticate(isOnline: boolean): Promise<boolean> {
    return isAuthenticatedOutcome(await this.authenticateWithOutcome(isOnline));
  }

  /**
   * Authenticate the user and report *why* it did or did not work (#26615).
   *
   * `authenticate()` stays as a boolean adapter for the existing callers, so nothing changes for
   * them. The outcome itself changes no behaviour either: every outcome except the two
   * authenticated ones still ends on the login page. It exists so the sync pipeline can report
   * which of the five situations a failed authentication actually was — the numbers that decide
   * whether "keep the session instead of logging out" is worth doing in a later step.
   *
   * Call configure before authenticate or the token might be set wrong.
   */
  public async authenticateWithOutcome(isOnline: boolean): Promise<AuthOutcome> {
    debug('authenticate');
    window.logger.addBreadcrumb({
      category: 'auth',
      message: 'call authenticate',
      data: {
        isOnline,
      },
    });
    if (!isOnline) {
      return this.authenticateOfflineWithOutcome();
    } else if (isOnline && !this.isCordova) {
      await this._WebOauthService.tryLogin();
    }
    debug('has valid access token', this._WebOauthService.hasValidAccessToken());
    const accessToken = await this.getAccessToken();

    if (!accessToken) {
      window.logger.error('no access token', new Error('no access token'));
      // No *usable* token is not the same as no token at all: an expired one can still be
      // refreshed. Both still end on /login today — but only one of them is a real logout.
      const outcome = (await this.hasAnyToken()) ? AuthOutcome.Unavailable : AuthOutcome.Unauthenticated;
      window.logger.addBreadcrumb({ category: 'auth', message: `authenticate without access token -> ${outcome}` });
      return outcome;
    }

    return this.loginFeathersApp(accessToken);
  }

  /**
   * True when any token material is present. Tells "logged out" apart from "cannot talk to the
   * backend right now". Never throws; an unknown state (e.g. the token read timed out) counts as
   * "no token" — the conservative answer.
   */
  public async hasAnyToken(): Promise<boolean> {
    return (await this.readTokenState()) === 'token-present';
  }
  /**
   * Get a valid access token.
   * Return null if no valid access token is available.
   * Token on mobile is valid with a time threshold of TOKEN_VALIDITY_BUFFER_IN_SECONDS.
   * Token in web is valid if not expired due to different plugins
   * @returns
   */
  public async getAccessToken(): Promise<string | null> {
    if (this.isCordova) {
      return this.getValidMobileAccessToken();
    }
    return this._WebOauthService.getAccessToken();
  }

  /**
   * Get a valid access token from the mobile oauth service.
   * Return null if no valid access token is available.
   * @returns
   */
  private async getValidMobileAccessToken(): Promise<string | null> {
    try {
      const tokenResponse = await this._mobileOAuthService.getValidToken();

      if (tokenResponse.isValid(TOKEN_VALIDITY_BUFFER_IN_SECONDS)) {
        return tokenResponse.accessToken;
      }
    } catch (error) {
      console.warn('Failed to obtain valid access token', error);
    }
    window.logger.error('no valid access token', new Error('no valid acess token'));
    return null;
  }

  private async loginFeathersApp(accessToken: string): Promise<AuthOutcome> {
    debug(`login with access token`, accessToken);
    window.logger.addBreadcrumb({ category: 'auth', message: 'loginFeathersApp called' });

    let authenticationResult: AlbertaAuthenticationResult;
    try {
      authenticationResult = await this.tryAccessTokenOrByRefreshToken(accessToken);
    } catch (error) {
      const isFeathersError = error.type === 'FeathersError';
      const isCriticalError = ['timeout', 'not-authenticated'].includes(error.className) === false;
      if (!isFeathersError || isCriticalError) {
        window.logger.error('AUTHSERVICE AUTHENTICATE', error);
      } else {
        console.error('expected feathers error happened', error);
      }
      if (isCriticalError) {
        window.logger.error('loginFeathersApp failed', new Error(JSON.stringify({ isFeathersError, isCriticalError })));
      } else {
        window.logger.info(`loginFeathersApp failed ${JSON.stringify({ isFeathersError, isCriticalError })}`);
      }
      const outcome = this.classifyFeathersAuthFailure(error);
      window.logger.addBreadcrumb({
        category: 'auth',
        message: `loginFeathersApp authentication failed | className=${error.className} isFeathersError=${isFeathersError} isCriticalError=${isCriticalError} outcome=${outcome}`,
      });
      return outcome;
    }

    const user = authenticationResult.user;
    const validation = validateAccount(user, 'user');
    if (!validation.isValid) {
      const reason = validation.reason;
      window.logger.error('user from auth is not valid', new Error(`user from auth result is not valid: ${reason}`));
      window.logger.addBreadcrumb({ category: 'auth', message: `loginFeathersApp user invalid: ${reason}` });
    }

    window.logger.addBreadcrumb({
      category: 'auth',
      // never dereference an object that was just reported as broken — this threw and took the
      // whole sync pipeline down with it (#26615)
      message: `loginFeathersApp auth result received, userId=${readAccountId(user)}`,
    });

    if (validation.isValid) {
      try {
        await this.storage.set('currentUser', user);
      } catch (error) {
        window.logger.error('loginFeathersApp failed to store currentUser', error);
        window.logger.addBreadcrumb({ category: 'auth', message: 'loginFeathersApp storage.set(currentUser) failed' });
      }
    } else {
      // Resolves the TODO from incident 24139: an incomplete account must not overwrite the last
      // known good copy, otherwise a temporary backend glitch turns into a forced logout on the
      // next offline start (#26615). The in-memory account below is still updated, so the running
      // session behaves exactly as before.
      await this.keepOrDropCachedAccount(user);
    }

    this._account$.next(user);
    this._accountReady.resolve(user);
    debug('updated authentication account', user);
    this.initializeTracker(user);
    this.observeTokenExpiration(authenticationResult.accessToken);
    window.logger.addBreadcrumb({ category: 'auth', message: 'loginFeathersApp success' });
    return AuthOutcome.Authenticated;
  }

  /**
   * Maps a failed Feathers authentication onto an outcome (#26615).
   *
   * Only a rejected token means the user is logged out. Everything else — a timeout because the
   * socket is dead, a 5xx during a deployment, a network error — leaves the session valid, and
   * `tryAccessTokenOrByRefreshToken()` has already exhausted its retries at this point. Unknown
   * errors deliberately land on `Unavailable`: misreporting a valid session as a logout is the
   * expensive mistake, the reverse is cheap.
   */
  private classifyFeathersAuthFailure(error: { code?: number; className?: string }): AuthOutcome {
    if (error?.code === 401 || error?.className === 'not-authenticated') {
      return AuthOutcome.Unauthenticated;
    }
    return AuthOutcome.Unavailable;
  }

  /**
   * Refreshes the access token using the mobile OAuth service.
   * This method is used specifically for mobile applications.
   * In web it returns false
   * @returns A promise that resolves to true if the token was refreshed successfully, false otherwise
   *
   * TODO(#26615, branch 08 refresh-orchestrator): the action-listener plumbing below exists only
   * because the library's refreshToken() reports its outcome solely through the action stream —
   * which MobileTokenPreservingAuthService already watches. Let the wrapper expose the outcome
   * (e.g. refreshTokenClassified(): 'success' | RefreshErrorKind) and this method shrinks to
   * single-flight + policy. The listener plumbing has attracted three bugs of the same species
   * (never-resolving promise, null-return race, stale-action replay). Single-flight also belongs
   * in the wrapper, so library-internal getValidToken() refreshes are serialized too. The
   * session policy (logout + reload on invalid-grant) stays HERE — the wrapper must not know
   * the app session.
   */
  public async refreshTokenMobile(): Promise<boolean> {
    debug('refresh token');

    // Return existing promise if refresh is already in progress
    if (this._refreshTokenPromise) {
      debug('refresh already in progress, returning existing promise');
      return this._refreshTokenPromise;
    }

    if (this.isCordova) {
      const oldAccessToken = await this._mobileAccessToken.promise;

      if (!oldAccessToken || oldAccessToken === 'dummy-accessToken') {
        debug(`refreshToken failed with old accessToken - ${oldAccessToken}`);
        return false;
      }

      // Held in a local as well: the action handlers below reset the field to null as soon as
      // they fire, and when that happens before the `return` at the end of this method the
      // caller used to receive `null` instead of the result. `null` is falsy, so a *successful*
      // refresh was reported as a failure and the Authenticator sent the user to /login (#26615).
      const refreshTokenPromise = new Promise<boolean>(resolve => {
        // addActionListener() replays the last recorded action to a new observer synchronously,
        // *before* attaching it (ionic-appauth keeps an action history, wired up in init()).
        // That replay describes the PREVIOUS refresh, not this one: a stale RefreshSuccess used
        // to resolve `true` before the refresh below even started — and detach an observer
        // variable that was still undefined, so the real action fired into a second, dangling
        // handler. A stale RefreshFailed would re-log the previous error, on invalid-grant even
        // logout() + reload(), before the new attempt had a chance. Ignore the replay (#26615).
        let live = false;
        const refreshTokenObserver = this._mobileOAuthService.addActionListener(async action => {
          if (!live) {
            return;
          }
          if (action.action === AuthActions.RefreshSuccess) {
            this._mobileOAuthService.removeActionObserver(refreshTokenObserver);
            debug(`refreshToken succeeded with new accessToken - ${action.tokenResponse.accessToken}`);
            this._refreshTokenPromise = null;
            resolve(true);
          }
          if (action.action === AuthActions.RefreshFailed) {
            this._mobileOAuthService.removeActionObserver(refreshTokenObserver);
            this._refreshTokenPromise = null;
            const kind = classifyRefreshError(action.error);
            this.logRefreshFailure(kind);

            // Only a rejected grant means the user is really logged out. A network failure,
            // a timeout or a 5xx leaves the session intact — MobileTokenPreservingAuthService has put the
            // token response back, so the next pipeline run can recover without the user
            // having to enter a password (#26615).
            if (kind === 'invalid-grant') {
              // resolve before the reload: the Authenticator awaits this promise and used to
              // hang forever here, which only the reload papered over.
              resolve(false);
              await this.logout();
              window.location.reload();
              return;
            }

            resolve(false);
          }
        });
        // the synchronous replay (if any) happened during addActionListener; from here on
        // every action is fresh
        live = true;
      });
      this._refreshTokenPromise = refreshTokenPromise;

      await this._mobileOAuthService.refreshToken();

      return refreshTokenPromise;
    }

    return false;
  }

  /**
   * Refreshes the access token on whatever platform we are on (#26615).
   *
   * The sync pipeline's retry used `refreshTokenMobile()`, which returns false in the browser —
   * so the web never had a refresh retry at all. A token that was already expired at pipeline
   * time therefore went straight to /login, even though the web login uses the authorization
   * code flow with `offline_access` and a silent refresh moments later would have succeeded.
   * `setupAutomaticSilentRefresh()` refreshes on a timer; this triggers one at exactly the
   * moment the pipeline needs a valid token, closing that timing gap.
   *
   * A refresh is only attempted when a refresh token exists: `OAuthService.refreshToken()`
   * rejects with "No refresh token" otherwise, and that rejection is indistinguishable from a
   * real failure. Any failure is swallowed and reported as `false`, so the worst case is exactly
   * today's behaviour (the caller falls through to /login).
   */
  public async tryRefresh(): Promise<boolean> {
    if (this.isCordova) {
      return this.refreshTokenMobile();
    }

    // setupAutomaticSilentRefresh() is a timer inside the library that we cannot see or join. If
    // it renewed the token a moment ago, refreshing again would spend a second refresh token — with
    // rotating refresh tokens that is an invalid_grant, i.e. a spurious "logout" in exactly the
    // window this method exists to close. A valid token means there is nothing left to do.
    if (this._WebOauthService.hasValidAccessToken()) {
      return true;
    }

    if (!this._WebOauthService.getRefreshToken()) {
      return false;
    }

    try {
      await this.refreshWebTokenOnce();
      return this._WebOauthService.hasValidAccessToken();
    } catch (error) {
      this.logWebRefreshFailure(error);
      return false;
    }
  }

  /**
   * Single-flight web refresh (#26615), the counterpart of the `_refreshTokenPromise` guard in
   * refreshTokenMobile(). `OAuthService.refreshToken()` itself does not dedupe: every call fires
   * its own request. Concurrent app callers — the pipeline retry and the 401 path — share one
   * request here. This cannot cover the library's own silent-refresh timer; tryRefresh() narrows
   * that with its hasValidAccessToken() pre-check, and owning the schedule outright is branch 08.
   *
   * Deliberately a separate field from `_refreshTokenPromise`: refreshTokenMobile() checks that
   * field before its platform check, so sharing it would let a web refresh leak into the mobile
   * method's early return.
   */
  private refreshWebTokenOnce(): Promise<TokenResponse> {
    if (this._webRefreshPromise) {
      return this._webRefreshPromise;
    }
    this._webRefreshPromise = this._WebOauthService.refreshToken().finally(() => {
      this._webRefreshPromise = null;
    });
    return this._webRefreshPromise;
  }

  /**
   * Same reporting policy as the mobile refresh (see logRefreshFailure): the breadcrumb always
   * carries the classified kind and the error text, a Sentry error only for a rejected grant. A
   * transient failure heals on the next pipeline run and must not bury the metric #26615 is
   * measured by. Swallowing the error is intended — the caller falls through to today's
   * behaviour — losing its content is not.
   */
  private logWebRefreshFailure(error: unknown): void {
    try {
      const text = describeRefreshError(error);
      const kind = classifyRefreshError(text);
      window.logger.addBreadcrumb({
        category: 'auth',
        message: `[26615] web token refresh failed | kind=${kind} | ${text}`,
      });
      if (kind === 'invalid-grant') {
        window.logger.error(
          'tryRefresh web RefreshFailed (invalid-grant)',
          new Error(`[26615] web refresh rejected by the authorization server: ${text}`)
        );
      }
    } catch {
      // diagnostics only — never interfere with the auth flow
    }
  }

  public async configure(): Promise<void> {
    return this.isCordova ? this.configureMobile() : this.configureBrowser();
  }

  public async login(email: string): Promise<void> {
    debug('login', email);
    if (email) {
      email = email.trim().toLowerCase();
    }

    if (this.isCordova) {
      const lastUser = await this.storage.get('currentUser');
      const authExtras: Record<string, string> = { login_hint: email };
      if (!lastUser || lastUser.email !== email) {
        debug('no stored user or email mismatch, forcing fresh login');
        authExtras.prompt = 'login';
      }
      await this._mobileOAuthService.signIn(authExtras);
    } else {
      // TODO: renenable if code flow works with login_hint
      // PR https://github.com/manfredsteyer/angular-oauth2-oidc/pull/938 is merged
      // this._oauthService.initLoginFlow(this.redirectUrl, email);
      this._WebOauthService.initLoginFlow(this.redirectUrl);
    }
  }

  public async logout(skipWindowReload = false): Promise<void> {
    debug('logout');

    try {
      await navigator?.serviceWorker.getRegistrations().then(async function (registrations) {
        for (const registration of registrations) {
          await registration.unregister();
        }
      });
    } catch (error) {}

    await this._feathersAppProvider.app.authentication.removeAccessToken();

    if (this._connectionStateService.getSocketConnectionState()) {
      try {
        this._tracker.trackLogout();
        await this._feathersAppProvider.app.logout();
      } catch (error) {
        window.logger.error('logout request at backend failed', error);
      }
    }
    await this.storage.remove('currentUser');
    // Reset accountReady for next login - create a new Deferred instance
    this._accountReady = new Deferred<IAuthenticatedAccount>();
    this._account$.next(null);
    this._isAuthenticated$.next(false);

    this._authenticationEventSubject.next({
      isAuthenticated: false,
    });
    this._tracker.clearIdentify();
    if (this._platform.is('hybrid') && this._sessionVault.vaultInitialized) {
      await this._sessionVault.clearVaultAndResetLockMode();
    }

    if (this._connectionStateService.getSocketConnectionState()) {
      this.isCordova ? await this._mobileOAuthService.signOut() : this._WebOauthService.logOut();
    } else if (!this._connectionStateService.getSocketConnectionState() && this.isCordova) {
      await this._mobileOAuthService.removeToken();
      if (!skipWindowReload) {
        window.location.reload();
      }
    }
  }

  private async tryAccessTokenOrByRefreshToken(accessToken: string): Promise<AlbertaAuthenticationResult> {
    await this._environmentService.ready;

    let lastTransientError: Error;
    let hasRefreshedToken = false;
    for (let attempt = 0; attempt <= MAX_TRANSIENT_AUTH_RETRIES; attempt++) {
      if (attempt > 0) {
        await this.waitBeforeRetry(attempt);
        window.logger.addBreadcrumb({
          category: 'auth',
          message: `retrying feathers authentication after transient error, attempt ${attempt}`,
        });
      }
      try {
        return await this.authenticateAtFeathers(accessToken);
      } catch (error) {
        let failedError = error;
        if (error.code === 401 && !hasRefreshedToken) {
          // Token was rejected by the backend: refresh once and retry with the new token.
          // Transient failures of the refreshed authentication re-enter the retry loop.
          hasRefreshedToken = true;
          try {
            accessToken = await this.getAccessTokenByRefreshToken();
          } catch (refreshError) {
            // The backend rejected the token and the refresh did not produce a new one. WHY it
            // failed decides what this is (#26615): a rejected grant — invalid_grant, i.e. the
            // refresh token expired, was revoked or the user was deactivated — means the user is
            // really logged out, and only the ORIGINAL 401 tells the caller so. Anything else —
            // IdP unreachable, timeout, deploy — means we could not refresh *right now*; the
            // refresh token may be perfectly valid, so the refresh error is propagated and
            // classifies as Unavailable instead of as a logout. Same policy as the mobile refresh
            // (refresh-error.ts): only a rejected grant is terminal.
            const refreshText = describeRefreshError(refreshError);
            const kind = classifyRefreshError(refreshText);
            window.logger.addBreadcrumb({
              category: 'auth',
              message: `[26615] refresh after 401 failed | kind=${kind} | ${refreshText}`,
            });
            if (kind === 'invalid-grant') {
              window.logger.error('tryAccessTokenOrByRefreshToken refresh rejected', refreshError);
              throw error;
            }
            throw refreshError;
          }
          try {
            return await this.authenticateAtFeathers(accessToken);
          } catch (refreshedError) {
            failedError = refreshedError;
          }
        }
        if (!isTransientAuthError(failedError)) {
          window.logger.error('tryAccessTokenOrByRefreshToken failed', failedError);
          throw failedError;
        }
        lastTransientError = failedError;
      }
    }

    window.logger.error(
      `tryAccessTokenOrByRefreshToken failed after ${MAX_TRANSIENT_AUTH_RETRIES} retries`,
      lastTransientError
    );
    throw lastTransientError;
  }

  private async authenticateAtFeathers(accessToken: string): Promise<AlbertaAuthenticationResult> {
    return this._feathersAppProvider.app.authenticate({
      strategy: 'fusionAuthJwt',
      accessToken,
      applicationId: this._environmentService.authConfig.authCodeFlowConfigWeb.clientId,
    });
  }

  private waitBeforeRetry(attempt: number): Promise<void> {
    const backoffDelay = AUTH_RETRY_BASE_DELAY_IN_MS * 2 ** (attempt - 1);
    return new Promise(resolve => setTimeout(resolve, backoffDelay));
  }

  public async setAuthenticated(isAuthenticated: boolean, isOnline?: boolean): Promise<void> {
    this._isAuthenticated$.next(isAuthenticated);
    this._authenticationEventSubject.next({ isAuthenticated, authOnline: isOnline });
    this._init.resolve();
    this._tracker.isAuthOnline = isOnline;
    this._tracker.isAuthenticated = isAuthenticated;
  }

  private async authenticateOfflineWithOutcome(): Promise<AuthOutcome> {
    const currentUser = await this.storage.get('currentUser');
    const validation = validateAccount(currentUser, 'currentUser');
    if (!validation.isValid) {
      const reason = validation.reason;
      // The account already in memory carries the session (#26615). The cache is only a copy of
      // it for the next process start: both are written together and the copy is always written
      // *after* the in-memory value, so the running process can never hold the staler one. A cache
      // that has gone missing while the app is running is therefore a storage problem, not an
      // authentication problem — and re-reading a copy of something we already have must not be
      // able to end the session.
      const inMemoryAccount = this._account$.value;

      // Collect the state *before* reporting: Sentry only attaches breadcrumbs that already exist
      // when captureException runs. Reporting first meant the token and storage state landed on
      // whatever event happened next instead of on this one (#26615).
      const tokenState = await this.reportAccountCacheMiss(reason, inMemoryAccount != null);

      if (inMemoryAccount != null) {
        // Nothing else to set up: _account$, _accountReady, the tracker and the token expiration
        // observer were all initialised when this account was loaded earlier in this session.
        return AuthOutcome.OfflineAuthenticated;
      }

      window.logger.error('authenticateOffline failed', new Error(`currentUser is not valid: ${reason}`));
      // A missing cache is not a logout as long as token material exists: the next online run can
      // fetch the account and repair the cache. Both outcomes still end on /login today — the
      // distinction makes the avoidable case countable (#26615).
      return tokenState === 'token-present' ? AuthOutcome.NeedsOnlineReauth : AuthOutcome.Unauthenticated;
    }
    try {
      const accessToken = this.isCordova
        ? await this._mobileAccessToken.promise
        : this._WebOauthService.getAccessToken();

      if (accessToken != null && accessToken !== 'dummy-accessToken') {
        this.observeTokenExpiration(accessToken);
        this._account$.next(currentUser);
        this._accountReady.resolve(currentUser);

        this.initializeTracker(currentUser);
      }
    } catch (error) {
      // Previously swallowed by a `return` inside `finally`; the result was the same, the error
      // just vanished. Logging it changes nothing for the caller.
      window.logger.error('authenticateOffline failed to set up the session', error);
    }

    // A valid cached account is enough to work offline, even when the token could not be read
    // (that only means no background refresh is scheduled).
    return AuthOutcome.OfflineAuthenticated;
  }

  /**
   * Decides what happens to the cached account when the backend returned an unusable one (#26615).
   *
   * Keeping the last known good copy is what protects the offline start, but it must never leak
   * one user's account into another user's session: if the broken object clearly belongs to a
   * different user than the cached one, the cache is dropped instead of kept.
   */
  private async keepOrDropCachedAccount(invalidAccount: unknown): Promise<void> {
    try {
      const cached = await this.storage.get('currentUser');
      const cachedId = readAccountId(cached);
      const incomingId = readAccountId(invalidAccount);

      if (cachedId != null && incomingId != null && cachedId !== incomingId) {
        await this.storage.remove('currentUser');
        window.logger.addBreadcrumb({
          category: 'auth',
          message: '[26615] invalid account for a different user — cached account dropped',
        });
        return;
      }

      window.logger.addBreadcrumb({
        category: 'auth',
        message: `[26615] invalid account not written, cached account kept (cached=${cachedId != null})`,
      });
    } catch (error) {
      window.logger.error('keepOrDropCachedAccount failed', error);
    }
  }

  /**
   * Records how a refresh failed so the share of avoidable logouts stays measurable (#26615).
   */
  private logRefreshFailure(kind: RefreshErrorKind): void {
    try {
      window.logger.addBreadcrumb({
        category: 'auth',
        message: `[26615] refresh failed | kind=${kind} online=${this._connectionStateService.isConnected}`,
      });

      // Only a rejected grant costs the user a full password login — that is the event worth an
      // error. Transient failures heal themselves on the next pipeline run; reporting each one
      // as an error would bury the very Sentry metric #26615 is measured by (the ticket already
      // notes the raw count overstates the problem). kind=transient stays countable through the
      // breadcrumb above.
      if (kind === 'invalid-grant') {
        window.logger.error(
          `refreshTokenMobile RefreshFailed (${kind})`,
          new Error(`[26615] refresh failed with kind=${kind}`)
        );
      }
    } catch {
      // diagnostics only — never interfere with the auth flow
    }
  }

  /**
   * Reports that `currentUser` was missing or invalid in Ionic Storage (IndexedDB) — #26615.
   *
   * Records whether an auth token still exists: a present token means the user never logged out,
   * the currentUser object was lost (suspected eviction of the WebView storage). The storage
   * generation verdict tells that apart from a fresh install, which a token alone cannot.
   *
   * The two outcomes are reported as *separate* Sentry issues on purpose:
   * - session kept from memory → the cache was lost but nobody noticed. Countable near-miss.
   * - forced logout → the user really was thrown out. Keeps its original message so the existing
   *   issue survives and from now on contains only the cases that actually hurt.
   *
   * Read-only checks, all errors swallowed, never changes the auth flow.
   */
  private async reportAccountCacheMiss(reason: string, sessionKeptFromMemory: boolean): Promise<TokenState> {
    const tokenState = await this.readTokenState();

    // A present token alone cannot distinguish an evicted storage from a fresh install (the
    // keychain survives a reinstall). The storage generation verdict can — see
    // StorageDiagnosticsService.
    const storageState = StorageDiagnosticsService.describe();

    // This breadcrumb is the only place where the full picture reaches Sentry for the cases that
    // do not get their own event (no-token, unknown). `data` keeps it machine readable instead of
    // forcing everyone to parse the message string.
    window.logger.addBreadcrumb({
      category: 'auth',
      message: sessionKeptFromMemory
        ? `authenticateOffline cache miss, session kept from memory | reason=${reason} tokenState=${tokenState} ${storageState}`
        : `authenticateOffline forced logout | reason=${reason} tokenState=${tokenState} ${storageState}`,
      data: {
        reason,
        tokenState,
        sessionKeptFromMemory,
        storageVerdict: storageDiagnostics.verdict,
        storageDriver: storageDiagnostics.driver,
        isCordova: this.isCordova,
      },
    });

    if (sessionKeptFromMemory) {
      // Own issue, own count: this is "the cache was lost and we caught it". Without it the loss
      // would be invisible from now on, because the user no longer notices anything.
      window.logger.error(
        'authenticateOffline cache miss, session kept from memory',
        new Error(
          `[26615] currentUser lost from storage while the app was running, session continued from memory | reason=${reason} tokenState=${tokenState} | ${storageState}`
        )
      );
      return tokenState;
    }

    if (tokenState === 'token-present') {
      window.logger.error(
        'authenticateOffline forced logout with token present',
        new Error(`[26615] Forced logout: currentUser lost from storage but auth token still present | ${storageState}`)
      );
    }
    return tokenState;
  }

  /**
   * Whether any OAuth token material is present, without touching the token itself.
   * Read-only, never throws, bounded: the mobile token deferred may still be pending, so the
   * read gives up after 1.5 s and reports `unknown` rather than stalling the auth flow.
   */
  private async readTokenState(): Promise<TokenState> {
    const TIMED_OUT = '[26615]-token-check-timed-out';
    try {
      if (!this.isCordova) {
        const hasToken = Boolean(this._WebOauthService.getAccessToken() || this._WebOauthService.getRefreshToken());
        return hasToken ? 'token-present' : 'no-token';
      }
      if (this._mobileAccessToken == null) {
        return 'unknown';
      }
      let timeoutHandle: ReturnType<typeof setTimeout>;
      const timeout = new Promise<string>(resolve => {
        timeoutHandle = setTimeout(() => resolve(TIMED_OUT), 1500);
      });
      const accessToken = await Promise.race([this._mobileAccessToken.promise, timeout]);
      clearTimeout(timeoutHandle);
      if (accessToken === TIMED_OUT) {
        return 'unknown';
      }
      return accessToken != null && accessToken !== 'dummy-accessToken' ? 'token-present' : 'no-token';
    } catch {
      return 'unknown';
    }
  }

  private async configureBrowser() {
    await this._environmentService.ready;
    debug('configure browser');
    if (!this._WebOauthService.tokenEndpoint) {
      // ! prevent reconfigure!
      this._WebOauthService.configure(this._environmentService.authConfig.authCodeFlowConfigWeb);
      await this._WebOauthService.loadDiscoveryDocument();
      this._WebOauthService.setupAutomaticSilentRefresh();
    }
  }

  private async configureMobile(): Promise<void> {
    // Overlapping calls join the in-flight run. Without this guard every call replaces
    // _mobileAccessToken, leaving earlier callers awaiting a deferred that never resolves.
    if (this._configureMobilePromise != null) {
      return this._configureMobilePromise;
    }

    this._configureMobilePromise = this.runConfigureMobile().finally(() => {
      this._configureMobilePromise = null;
    });

    return this._configureMobilePromise;
  }

  private async runConfigureMobile(): Promise<void> {
    const mobileAccessToken = new Deferred<string>();
    this._mobileAccessToken = mobileAccessToken;
    const authConfigured = new Deferred<void>();

    // events$ is backed by a BehaviorSubject: it synchronously replays the last action to
    // new subscribers. Drop that replay so only events emitted by loadTokenFromStorage()
    // below count — otherwise a stale action of a previous run resolves this run.
    let isSynchronousReplay = true;
    const subscription = this._mobileOAuthService.events$
      .pipe(
        filter(
          action =>
            !isSynchronousReplay &&
            (action?.action === AuthActions.LoadTokenFromStorageSuccess ||
              action?.action === AuthActions.LoadTokenFromStorageFailed)
        ),
        take(1)
      )
      .subscribe(action => {
        // async work wrapped so a thrown fallback can never leave the deferreds unsettled
        void (async () => {
          try {
            if (action.action === AuthActions.LoadTokenFromStorageSuccess) {
              mobileAccessToken.resolve(action.tokenResponse.accessToken);
            } else {
              mobileAccessToken.resolve(await this.getMobileAccessTokenFallback());
            }
          } catch (error) {
            window.logger.error('configureMobile: failed to resolve access token', error);
            mobileAccessToken.resolve('dummy-accessToken');
          } finally {
            authConfigured.resolve();
          }
        })();
      });
    // the synchronous replay (if any) happened during subscribe; from here on events are fresh
    isSynchronousReplay = false;

    try {
      await this._mobileOAuthService.loadTokenFromStorage();
    } catch (error) {
      window.logger.error('configureMobile: loadTokenFromStorage failed', error);
      subscription.unsubscribe();
      try {
        mobileAccessToken.resolve(await this.getMobileAccessTokenFallback());
      } catch (fallbackError) {
        window.logger.error('configureMobile: failed to resolve access token', fallbackError);
        mobileAccessToken.resolve('dummy-accessToken');
      } finally {
        authConfigured.resolve();
      }
    }

    return authConfigured.promise;
  }

  private async getMobileAccessTokenFallback(): Promise<string> {
    const token = await this.getValidMobileAccessToken();
    return token ?? 'dummy-accessToken';
  }

  private observeTokenExpiration(accessToken) {
    debug('setup token expiration observer');
    if (this._tokenExpiredSubscription && !this._tokenExpiredSubscription.closed) {
      this._tokenExpiredSubscription.unsubscribe();
    }

    if (this.isCordova) {
      this._tokenExpiredSubscription = isTokenAboutToExpire(accessToken, this._ngZone).subscribe({
        complete: async () => {
          if (this._connectionStateService.getSocketConnectionState()) {
            debug('token will expire... refresh');
            await this.refreshTokenMobile();
            debug('accessToken refreshed');
            await this.configure();
            await this.authenticate(this._connectionStateService.isConnected);
            debug('authenticate called with new accessToken');
          }
        },
        error: err => console.error(err),
      });
    } else {
      this._WebOauthService.events
        .pipe(
          filter(e => e.type === 'token_received'),
          take(1)
        )
        .subscribe(async () => {
          await this.authenticate(this._connectionStateService.isConnected);
        });
    }
  }

  /**
   * Exchanges the refresh token for a new access token, or throws when that did not happen.
   *
   * Mobile used to fall through after a failed refreshTokenMobile() and hand back the OLD token —
   * the one the backend had just rejected — which only earned a second 401 and made a transient
   * refresh failure indistinguishable from a rejected grant. It now throws, so the caller
   * classifies the failure exactly like on web (#26615). refreshTokenMobile() handles a rejected
   * grant itself (logout + reload), so a `false` here is a transient or unknown failure.
   *
   * The former `new Promise(async resolve => …)` also never rejected: an exception inside the
   * executor left the promise pending forever instead of surfacing.
   */
  private async getAccessTokenByRefreshToken(): Promise<string> {
    if (this._platform.is('hybrid')) {
      const refreshed = await this.refreshTokenMobile();
      if (!refreshed) {
        throw new Error('mobile token refresh did not yield a new token');
      }
      await this.configureMobile();
      return this._mobileAccessToken.promise;
    }
    // shares the in-flight request with tryRefresh() — see refreshWebTokenOnce() (#26615)
    const tokenResponse = await this.refreshWebTokenOnce();
    return tokenResponse.access_token;
  }

  //#endregion

  //#region ACCESS METHODS TO AUTHENTICATED USER OBJECT

  public async getCurrentUserICSToken(): Promise<string | null> {
    const account = await this.getAccount();
    if (!account?.icsToken) {
      const errorMessage = 'CurrentUserICSToken not found';
      window.logger.error(errorMessage, new Error(errorMessage));
      return null;
    }
    return account?.icsToken;
  }

  public async getCurrentUserId(): Promise<string> {
    const account = await this.getAccount();
    return account._id;
  }

  public async canUseAlbertaLight(): Promise<boolean> {
    const account = await this.getAccount();
    return account?.authorization?.hydratedOrganization?.alberta?.canUseAlbertaLight === true;
  }

  public async getCurrentUserGroups(): Promise<string[] | null> {
    const account = await this.getAccount();
    if (!account?.groups) {
      const errormessage = `{user groups not found on auth object}`;
      window.logger.error(errormessage, new Error(errormessage));
      return null;
    }
    return account.groups;
  }

  public async getCurrentUserRegions(): Promise<IRegion[] | null> {
    const account = await this.getAccount();
    if (!account?.authorization?.regions) {
      const errormessage = `{user regions not found on auth object}`;
      window.logger.error(errormessage, new Error(errormessage));
      return null;
    }
    return account.authorization.regions;
  }

  /**
   * Returns the tenantId of the current user in the organization property.
   * CAREFUL: THIS IS THE NAME OF THE TENANT, NOT THE ID!
   * EXAMPLE: '1TLabs'
   * @returns The tenantId of the current user.
   */
  public async getCurrentUserTenantName(): Promise<string | null> {
    const account = await this.getAccount();
    if (!account?.authorization?.hydratedOrganization?.tenantId) {
      window.logger.error('getCurrentUserTenantId failed', new Error('getCurrentUserTenantId failed'));
      return null;
    }
    return account.authorization.hydratedOrganization.tenantId;
  }

  public async getCurrentUserTenantId(): Promise<string | null> {
    const account = await this.getAccount();
    return account?.tenantId;
  }

  public async getCurrentUserOrganization(): Promise<hydratedOrganization | null> {
    const account = await this.getAccount();
    return account.authorization?.hydratedOrganization;
  }

  public async resetRights(rights): Promise<void> {
    if (!rights) {
      return;
    }
    const account = await this.getAccount();
    if (!account?.authorization) {
      return;
    }

    account.authorization.rightSets = rights;
    this._account$.next(account);
    await this.persistAccount(account, 'resetRights');
  }

  public async setRegions(regions: IRegion[]): Promise<void> {
    const account = await this.getAccount();
    if (!account?.authorization) {
      return;
    }

    account.authorization.regions = regions;
    this._account$.next(account);
    await this.persistAccount(account, 'setRegions');
  }

  public async resetCurrentUser(user: IUser, regions: IRegion[]): Promise<void> {
    const account = await this.getAccount();
    if (!account?.authorization) {
      return;
    }
    // Spreading the synced IUser over the account used to be able to replace `authorization`
    // wholesale — the users collection carries no hydratedOrganization, so the cached account
    // silently lost it and the next offline start forced a logout (#26615). `authorization`
    // is therefore excluded from the merge and updated explicitly below.
    const updatedAccount: IAuthenticatedAccount = {
      ...account,
      ...omit(user, ['organization', 'authorization']),
      authorization: { ...account.authorization, regions },
    };
    this._account$.next(updatedAccount);
    await this.persistAccount(updatedAccount, 'resetCurrentUser');
  }

  /**
   * Writes the account to storage, but only when it is still complete (#26615).
   * A partial write is worse than no write: the stale-but-valid copy in storage is what keeps
   * the offline start working.
   */
  private async persistAccount(account: IAuthenticatedAccount, origin: string): Promise<void> {
    const validation = validateAccount(account, 'account');
    if (!validation.isValid) {
      window.logger.error(
        `${origin} refused to persist an invalid account`,
        new Error(`[26615] ${origin}: ${validation.reason}`)
      );
      return;
    }

    try {
      await this.storage.set('currentUser', account);
    } catch (error) {
      window.logger.error(`${origin} failed to store currentUser`, error);
    }
  }

  public async getUserPermission(domain: string): Promise<number> {
    const account = await this.getAccount();
    if (!account?.authorization?.rightSets) {
      return 0;
    }
    const rightSet = account.authorization.rightSets.find(auth => auth.domain === domain);
    return rightSet ? rightSet.permission : 0;
  }

  public async userHasPermission(domain: string, permission: number): Promise<boolean> {
    const account = await this.getAccount();
    if (!account?.authorization?.rightSets) {
      return false;
    }

    return account.authorization.rightSets.some(
      rightSet => rightSet.permission >= permission && rightSet.domain === domain
    );
  }
  //#endregion

  //#region HELPER METHODS

  private initializeTracker(account: any) {
    try {
      this._tracker.identify(account._id, account.email, account.organization);
    } catch (error) {
      window.logger.error('tracker initialization failed', error);
    }
  }
  //#endregion
}
